/**
 * React Query hooks for the Well Testing AI drafts (WT-6, fieldiq-api#1261).
 * Keys start ['wellTesting'] like every WT hook, so a WT write elsewhere
 * refreshes these too; reads spread LOAD_FAILURE_QUERY_OPTIONS (a 403 is an
 * answer, not a flake). A run, an approve and a dismiss each invalidate the WT
 * prefix — the register and the stored run both move.
 *
 * Approve and dismiss go through the WT doors (POST /well-testing/ai/digests/
 * :id/approve | /dismiss), which take an edited narrative.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { invalidateWt } from './useWellTesting';
import type { WtAiApproveBody, WtAiRunResult, WtAiStoredRun } from '../types/wt-ai';

/** `{ data }` → data, or null. Never the envelope. */
function unwrap<T>(body: unknown): T | null {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return null;
  const d = (body as { data?: unknown }).data;
  return d === undefined || d === null ? null : (d as T);
}

export const wtAiKeys = {
  digests: (agent?: string, jobId?: string) => ['wellTesting', 'ai', 'digests', agent ?? '', jobId ?? ''] as const,
  digest: (id: string) => ['wellTesting', 'ai', 'digest', id] as const,
};

export function useWtAiDigests(filters: { agent?: string; jobId?: string } = {}, enabled = true) {
  const { agent, jobId } = filters;
  return useQuery({
    queryKey: wtAiKeys.digests(agent, jobId),
    queryFn: async () => {
      const params: { agent?: string; jobId?: string } = {};
      if (agent) params.agent = agent;
      if (jobId) params.jobId = jobId;
      const d = unwrap<WtAiStoredRun[]>((await api.wellTesting.ai.digests(Object.keys(params).length ? params : undefined)).data);
      return Array.isArray(d) ? d : [];
    },
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWtAiDigest(id: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: wtAiKeys.digest(id ?? ''),
    queryFn: async () => unwrap<WtAiStoredRun>((await api.wellTesting.ai.digest(id as string)).data),
    enabled: enabled && !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export type WtAiRunRequest =
  | { agent: 'wt_shift_summary'; jobId: string; shift: 'day' | 'night'; workDate?: string }
  | { agent: 'wt_ticket_preflight'; ticketId: string }
  | { agent: 'wt_ticket_preflight'; jobId: string }
  | { agent: 'wt_compliance_digest' };

export function useRunWtAi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: WtAiRunRequest): Promise<WtAiRunResult | null> => {
      const ai = api.wellTesting.ai;
      let res;
      if (req.agent === 'wt_shift_summary') {
        res = await ai.shiftSummary({ job_id: req.jobId, shift: req.shift, ...(req.workDate ? { work_date: req.workDate } : {}) });
      } else if (req.agent === 'wt_ticket_preflight') {
        res = 'ticketId' in req ? await ai.ticketPreflight(req.ticketId) : await ai.jobPreflight(req.jobId);
      } else {
        res = await ai.complianceDigest();
      }
      return unwrap<WtAiRunResult>(res.data);
    },
    onSuccess: () => invalidateWt(qc),
  });
}

export function useReviewWtAi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: { id: string; action: 'approve'; edit?: WtAiApproveBody } | { id: string; action: 'dismiss' }) => {
      const res = req.action === 'approve'
        ? await api.wellTesting.ai.approve(req.id, req.edit ?? {})
        : await api.wellTesting.ai.dismiss(req.id);
      return unwrap<WtAiStoredRun>(res.data);
    },
    onSettled: () => invalidateWt(qc),
  });
}
